<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin:0 10px 0; height: 30px; width: 30px;"
                  :loading="loadThePrintBtn"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 0; height: 30px; width: 30px"
                  fab
                  :loading="loadTheExportBtn"
                  flat
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row justify-end>
          <v-flex xs3 pa-2 shrink>
            <v-autocomplete
              v-if="hasPermissions(['outlet_select'])"
              v-model="outlet_id"
              :label="`${$t('Outlet')} *`"
              hide-details
              :items="outletsList"
              @input="updateDatatableResult()"
              data-vv-name="outlet"
              :error="errors.has('outlet')"
              v-validate="'required'"
            />
          </v-flex>
          <v-flex pa-2 shrink>
            <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="date"
                :label="$t('Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="date"
                :error="errors.has('date')"
                v-validate="'required|date_format:yyyy-MM-dd'"
              />
              <v-date-picker @change="dateMenu = false" v-model="date" @input="updateDatatableResult()" :max="today" />
            </v-menu>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card px-4 pb-4>
      <v-data-table
        :headers="headers"
        :items="resultData.accounts"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.journal_code }}</td>
            <td>{{ props.item.journal_date }}</td>
            <td>{{ $t(props.item.account_name.trim()) }}</td>
            <td>{{ props.item.debit | currency }}</td>
            <td>{{ props.item.credit | currency }}</td>
          </tr>
        </template>

        <template v-slot:footer>
          <tr>
            <td colspan="3">
              <strong>{{ $t('Total') }}</strong>
            </td>
            <td>
              <strong>{{ resultData.total_debit }}</strong>
            </td>
            <td>
              <strong>{{ resultData.total_credit | currency }}</strong>
            </td>
          </tr>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>
    </v-card>

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Axios from 'axios'
import * as helpers from '@/mixins/helperFunctions'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      dialogs: {
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      name: this.$route.query.name,
      id: this.$route.query.id,
      balance: this.$route.query.balance,
      dateMenu: false,
      today: this.$moment()
        .format()
        .substr(0, 10),
      date: this.$route.query.date,
      outlet_id: this.$route.query.outlet_id,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'journal_code',
      headers: [
        { text: this.$t('NO.'), value: 'journal_code' },
        { text: this.$t('Date'), value: 'journal_date' },
        { text: this.$t('Account Name'), value: 'account_name' },
        {
          text: `${this.$t('Debit')} (${localStorage.systemCurrency})`,
          value: 'debit'
        },
        {
          text: `${this.$t('Credit')} (${localStorage.systemCurrency})`,
          value: 'credit'
        }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: `${this.$t('Ledger Account Detail')}: <strong class="title-highlight">${this.name} (${this.$t(
        'Balance'
      )}: ${this.balance})</strong>`,
      icon: 'fa-chart-line'
    })

    this.fetchOutletList()
    this.updateDatatableResult()
  },

  computed: {
    ...mapGetters({
      resultData: 'generalLedger/getLedgerDetailList',
      outletsList: 'outlets/getFullReportOutletsList'
    })
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'generalLedger/updateLedgerDetailList',
      fetchOutletList: 'outlets/setFullOutletsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`ledger_account_detail/${this.id}`, {
        params: {
          search: this.search,
          date: this.date,
          outlet_id: this.outlet_id
        }
      })
        .then(response => {
          this.tableBody = response.data.data.accounts
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        id: this.id,
        date: this.date,
        outlet_id: this.outlet_id,
        search: this.search
      }
      this.updateResult(payload)
        .then(() => {
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
      this.updateDatatableResult()
    }
  }
}
</script>

<style scoped />
